(() => {
    // Prevent double attach
    if (window.util) return;

    // ------------------------------------------------------------
    // PRIVATE HELPERS
    // ------------------------------------------------------------

    /** @param {unknown} v */
    const normStr = (v) => (v == null ? '' : String(v).trim());
    /** @param {unknown} v */
    const normLower = (v) => normStr(v).toLowerCase();

    /** @param {unknown} n */
    const isFiniteNumber = (n) => typeof n === 'number' && Number.isFinite(n);

    /** Plain object: `{}`, `Object.create(null)`, not arrays, dates, etc. @param {unknown} v */
    const isPlainObject = (v) => {
        if (v === null || typeof v !== 'object') return false;
        const proto = Object.getPrototypeOf(v);
        return proto === null || proto === Object.prototype;
    };

    /**
     * Plain object only: removes selected own enumerable keys by value.
     * By default, only keys with `null` values are removed.
     *
     * @param {unknown} v
     * @param {{
     *   deep?: boolean,
        *   omitEmptyArrays?: boolean,
        *   omitEmptyStrings?: boolean,
        *   omitEmptyObjects?: boolean,
        * }} [options]
        * @returns {unknown}
        */
    const cleanJson = (v, options = {}) => {
        const deep = options.deep === true;
        const omitEmptyArrays = options.omitEmptyArrays === true;
        const omitEmptyStrings = options.omitEmptyStrings === true;
        const omitEmptyObjects = options.omitEmptyObjects === true;
    
        /**
            * @param {unknown} val
            * @returns {boolean}
            */
        const shouldOmitValue = (val) => {
            if (val === null) return true;
            if (omitEmptyArrays && Array.isArray(val) && val.length === 0) return true;
            if (omitEmptyStrings && typeof val === 'string' && val === '') return true;
            if (omitEmptyObjects && isPlainObject(val)) {
                const o = /** @type {Record<string, unknown>} */ (val);
                if (Object.keys(o).length === 0) return true;
            }
            return false;
        };
    
        /**
            * @param {unknown} node
            * @returns {unknown}
            */
        const walk = (node) => {
            if (Array.isArray(node)) {
                const nextArray = node.map((item) => {
                    if (isPlainObject(item) || Array.isArray(item)) return walk(item);
                    return item;
                });
    
                return nextArray;
            }
    
            if (isPlainObject(node)) {
                const input = /** @type {Record<string, unknown>} */ (node);
                /** @type {Record<string, unknown>} */
                const output = {};
    
                Object.keys(input).forEach((key) => {
                    const rawValue = input[key];
                    const nextValue = (isPlainObject(rawValue) || Array.isArray(rawValue))
                        ? walk(rawValue)
                        : rawValue;
    
                    if (shouldOmitValue(nextValue)) return;
                    output[key] = nextValue;
                });
    
                return output;
            }
    
            return node;
        };
    
        if (!isPlainObject(v)) return v;
    
        if (!deep) {
            const input = /** @type {Record<string, unknown>} */ (v);
            /** @type {Record<string, unknown>} */
            const output = {};
    
            Object.keys(input).forEach((key) => {
                const value = input[key];
                if (shouldOmitValue(value)) return;
                output[key] = value;
            });
    
            return output;
        }
    
        return walk(v);
    };

    /** @param {number} n */
    const pad2 = (n) => String(n).padStart(2, '0');

    /** @param {unknown} v */
    const toNumber = (v) => {
        if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
        const n = Number(String(v).trim());
        return Number.isFinite(n) ? n : NaN;
    };

    // A forgiving "year" normalizer for 2-digit years
    // (Assumes 00-69 => 2000-2069, 70-99 => 1970-1999)
    /** @param {string | number} yy */
    const normalize2DigitYear = (yy) => {
        const n = Number(yy);
        if (!Number.isFinite(n)) return null;
        return n <= 69 ? 2000 + n : 1900 + n;
    };

    // Build ISO string without timezone (treated as local by Date)
    /**
     * @param {{
     *   y: number,
     *   m: number,
     *   d: number,
     *   hh?: number,
     *   mm?: number,
     *   ss?: number
     * }} parts
     */
    const toIsoLocal = ({ y, m, d, hh = 0, mm = 0, ss = 0 }) => {
        return `${y}-${pad2(m)}-${pad2(d)}T${pad2(hh)}:${pad2(mm)}:${pad2(ss)}`;
    };

    // ------------------------------------------------------------
    // DATE PARSING (best-effort, low deps)
    // ------------------------------------------------------------
    // Goals:
    // - accept:
    //   - seconds (%s) and milliseconds (%ms)
    //   - YYYY-MM-DD
    //   - YYYY-MM-DDTHH:mm:ss
    //   - ISO w/ timezone: YYYY-MM-DDTHH:mm:ssZ or ...-08:00 or ...+05:30
    //   - "February 3, 2026" / "Feb 3, 2026" / "February 3" / "Feb 3"
    //   - "2/3" / "2/3/26" / "2/3/2026"  (assumes M/D[/YY[YY]])
    //
    // Output:
    // - Date (or null)
    // - epoch ms (or NaN)
    // - epoch seconds (or NaN)

    const MONTHS = new Map([
        ['jan', 1], ['january', 1],
        ['feb', 2], ['february', 2],
        ['mar', 3], ['march', 3],
        ['apr', 4], ['april', 4],
        ['may', 5],
        ['jun', 6], ['june', 6],
        ['jul', 7], ['july', 7],
        ['aug', 8], ['august', 8],
        ['sep', 9], ['sept', 9], ['september', 9],
        ['oct', 10], ['october', 10],
        ['nov', 11], ['november', 11],
        ['dec', 12], ['december', 12],
    ]);

    /** @param {unknown} input */
    function parseEpochLike(input) {
        // If number-like:
        // - 10 digits-ish => seconds
        // - 13 digits-ish => ms
        const raw = normStr(input);
        if (!raw) return null;

        // allow numeric types too
        const n = typeof input === 'number' ? input : toNumber(raw);
        if (!Number.isFinite(n)) return null;

        // Heuristic: seconds vs ms
        // - If >= 1e12 -> ms
        // - Else if >= 1e9 -> seconds (modern epoch)
        // - Else ambiguous; treat as ms only if small but explicitly flagged elsewhere (we won't)
        if (Math.abs(n) >= 1e12) {
            return new Date(n);
        }
        if (Math.abs(n) >= 1e9) {
            return new Date(n * 1000);
        }
        return null;
    }

    /** @param {unknown} input */
    function parseIsoLike(input) {
        const raw = normStr(input);
        if (!raw) return null;

        // Accept ISO-ish directly. JS Date parses:
        // - YYYY-MM-DD
        // - YYYY-MM-DDTHH:mm[:ss[.sss]](Z|±HH:MM)
        // - and many variants
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return null;
        return d;
    }

    /** @param {unknown} input */
    function parseSlashDate(input) {
        // M/D or M/D/YY or M/D/YYYY, optionally with time " M/D/YYYY HH:MM[:SS]"
        const raw = normStr(input);
        if (!raw) return null;

        const match = raw.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?(?:\s+(\d{1,2})(?::(\d{2}))(?::(\d{2}))?)?$/);
        if (!match) return null;

        const m = Number(match[1]);
        const d = Number(match[2]);
        let y = match[3] ? Number(match[3]) : null;

        const hh = match[4] ? Number(match[4]) : 0;
        const mm = match[5] ? Number(match[5]) : 0;
        const ss = match[6] ? Number(match[6]) : 0;

        if (!Number.isFinite(m) || !Number.isFinite(d)) return null;
        if (y != null && !Number.isFinite(y)) return null;

        // Year defaults:
        // - If missing year, default to current year
        //   (You can change this rule if you prefer "next occurrence" behavior.)
        if (y == null) {
            y = new Date().getFullYear();
        } else if (match[3] && String(match[3]).length === 2) {
            y = normalize2DigitYear(match[3]);
        }

        if (!y) return null;

        // Construct as local time
        const isoLocal = toIsoLocal({ y, m, d, hh, mm, ss });
        const date = new Date(isoLocal);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    /** @param {unknown} input */
    function parseMonthNameDate(input) {
        // "February 3, 2026" / "Feb 3, 2026" / "February 3" / "Feb 3"
        // Optionally with time: "Feb 3, 2026 13:00[:00]" (local)
        const raw = normStr(input);
        if (!raw) return null;

        // Normalize commas and extra spaces
        const clean = raw.replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
        const parts = clean.split(' ');

        // Expect: Month Day [Year] [Time]
        if (parts.length < 2) return null;

        const monthToken = normLower(parts[0]);
        const month = MONTHS.get(monthToken);
        if (!month) return null;

        // Day may have suffix like 3rd, 1st
        const dayToken = (parts[1] || '').replace(/(st|nd|rd|th)$/i, '');
        const day = Number(dayToken);
        if (!Number.isFinite(day)) return null;

        // Year optional
        let year = null;
        let timeToken = null;

        if (parts.length >= 3) {
            // If the next token looks like a year
            const nextToken = parts[2] || '';
            if (/^\d{4}$/.test(nextToken) || /^\d{2}$/.test(nextToken)) {
                year = nextToken.length === 2 ? normalize2DigitYear(nextToken) : Number(nextToken);
                timeToken = parts[3] || null;
            } else {
                // No year; maybe time
                timeToken = parts[2] || null;
            }
        }

        if (!year) year = new Date().getFullYear();

        let hh = 0, mm = 0, ss = 0;
        if (timeToken && /^\d{1,2}:\d{2}(:\d{2})?$/.test(timeToken)) {
            const tParts = timeToken.split(':').map(Number);
            hh = tParts[0] || 0;
            mm = tParts[1] || 0;
            ss = tParts[2] || 0;
        }

        const isoLocal = toIsoLocal({ y: year, m: month, d: day, hh, mm, ss });
        const date = new Date(isoLocal);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    /** @param {unknown} input */
    function parseAnyDate(input) {
        if (input instanceof Date) {
            return Number.isNaN(input.getTime()) ? null : input;
        }

        // 1) epoch heuristic
        const epoch = parseEpochLike(input);
        if (epoch) return epoch;

        // 2) slash formats
        const slash = parseSlashDate(input);
        if (slash) return slash;

        // 3) month name formats
        const monthName = parseMonthNameDate(input);
        if (monthName) return monthName;

        // 4) ISO-like / native Date parsing (last, since it's permissive)
        const iso = parseIsoLike(input);
        if (iso) return iso;

        return null;
    }

    // ------------------------------------------------------------
    // PUBLIC API (minimal global)
    // ------------------------------------------------------------

    const util = {
        clone: {
            /** @param {unknown} v */
            obj: (v) => {
                if (!isPlainObject(v)) return null;
                return JSON.parse(JSON.stringify(v));
            },
        },
        normalize: {
            /** @param {unknown} v */
            str: (v) => normStr(v),
            /** @param {unknown} v */
            text: (v) => normStr(v).replace(/\s+/g, ' ').trim(),
            /** @param {unknown} v */
            lower: (v) => normLower(v),
            /** @param {unknown} v */
            compactLower: (v) => normLower(v).replace(/\s+/g, ''),
            /**
             * True when `v` is meaningfully “present” (not null/undefined, not blank string, not empty collection,
             * not NaN/±Infinity, not invalid Date, not empty plain object).
             * - string: trimmed length > 0
             * - number: finite (0 counts as present)
             * - boolean / bigint / symbol / function: always true
             * - array: length > 0
             * - Map / Set: size > 0
             * - Date: valid (getTime() is finite)
             * - ArrayBuffer view (TypedArray, DataView): byteLength > 0
             * - plain object: at least one own key (string or symbol)
             * - other objects (RegExp, DOM nodes, class instances, WeakMap/WeakSet, …): true
             * @param {unknown} v
             * @returns {boolean}
             */
            exists: (v) => {
                if (v == null) return false;

                if (typeof v === 'string') return v.trim().length > 0;
                if (typeof v === 'number') return Number.isFinite(v);
                if (typeof v === 'boolean' || typeof v === 'bigint' || typeof v === 'symbol') return true;
                if (typeof v === 'function') return true;

                if (v instanceof String) return normStr(v).length > 0;
                if (v instanceof Number) return Number.isFinite(Number(v));
                if (v instanceof Boolean) return true;

                if (Array.isArray(v)) return v.length > 0;
                if (v instanceof Map || v instanceof Set) return v.size > 0;
                if (v instanceof WeakMap || v instanceof WeakSet) return true;

                if (v instanceof Date) return Number.isFinite(v.getTime());

                if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(v)) {
                    return v.byteLength > 0;
                }

                if (isPlainObject(v)) return Reflect.ownKeys(v).length > 0;

                return true;
            },
            /**
             * @param {unknown} v
             * @param {{ allowFloat?: boolean, fallback?: number }} [options]
             */
            number: (v, options = {}) => {
                const { allowFloat = true, fallback = NaN } = options;
                const n = toNumber(v);

                if (!Number.isFinite(n)) return fallback;
                if (!allowFloat && !Number.isInteger(n)) return fallback;

                return n;
            },
            /**
             * @param {unknown} v
             * @param {{ fallback?: number }} [options]
             */
            integer: (v, options = {}) => {
                const { fallback = NaN } = options;
                const n = toNumber(v);

                if (!Number.isFinite(n)) return fallback;

                return Number.isInteger(n) ? n : Math.trunc(n);
            },
            /**
             * @param {unknown} v
             * @param {{ fallback?: number }} [options]
             */
            nonNegativeInteger: (v, options = {}) => {
                const { fallback = 0 } = options;
                const n = util.normalize.integer(v, { fallback });

                if (!Number.isFinite(n) || n < 0) return fallback;

                return n;
            },
            /**
             * Normalize value to boolean.
             * @param {unknown} v
             * @param {{ fallback?: boolean }} [options]
             * @returns {boolean}
             */
            boolean: (v, options = {}) => {
                const { fallback = false } = options;
                if (typeof v === "boolean") return v;
                if (typeof v === "number") return v !== 0 && !Number.isNaN(v);
                if (typeof v === "string") {
                    const s = v.trim().toLowerCase();
                    if (["true", "yes", "on", "1"].includes(s)) return true;
                    if (["false", "no", "off", "0", ""].includes(s)) return false;
                }
                if (v == null) return false;
                if (typeof v === "object") return true;
                return fallback;
            },
        },
        url: {
            /** @param {string} value */
            parse: (value) => {
                try {
                    return new URL(value);
                } catch (error) {
                    return null;
                }
            },
            /** @param {string} value @param {string} name */
            readParam: (value, name) => {
                const url = util.url.parse(value);
                if (!url) return null;
                return url.searchParams.get(name);
            },
            /** @param {string} value @param {string} name @param {string} paramValue */
            setParam: (value, name, paramValue) => {
                const url = util.url.parse(value);
                if (!url) return null;
                url.searchParams.set(name, paramValue);
                return url.toString();
            },
            /** @param {string} value @param {string} name */
            deleteParam: (value, name) => {
                const url = util.url.parse(value);
                if (!url) return null;
                if (!url.searchParams.has(name)) return url.toString();
                url.searchParams.delete(name);
                return url.toString();
            },
        },
        data: {
            /**
             * @param {HTMLScriptElement | null | undefined} script
             * @param {unknown} [fallback=null]
             */
            parseJsonScript: (script, fallback = null) => {
                if (!(script instanceof HTMLScriptElement)) return fallback;

                const raw = script.textContent?.trim();
                if (!raw) return fallback;

                try {
                    return JSON.parse(raw);
                } catch (error) {
                    console.warn('[util.data] JSON parse failed', error);
                    return fallback;
                }
            },

            /**
             * One `Map` entry per distinct key value (last row wins). Skips items that are not plain objects.
             * @param {unknown} arr
             * @param {string} propKey - own property read from each object (`item[propKey]`)
             * @param {{ normalizeText?: boolean, normalizeNumber?: boolean }} [options]
             * @returns {Map<unknown, Record<string, unknown>>}
             */
            uniqueMapBy(arr, propKey, options = {}) {
                const out = new Map();
                if (!Array.isArray(arr) || typeof propKey !== 'string' || !propKey) return out;

                const normalizeText = options.normalizeText === true;
                const normalizeNumber = options.normalizeNumber === true;

                /** @param {unknown} raw */
                const mapKey = (raw) => {
                    let k = raw;
                    if (normalizeNumber) {
                        const n = util.normalize.number(k);
                        if (Number.isFinite(n)) k = n;
                    }
                    if (normalizeText && typeof k === 'string') {
                        k = util.normalize.text(k).toLowerCase();
                    }
                    return k;
                };

                for (const item of arr) {
                    if (!isPlainObject(item)) continue;
                    const rec = /** @type {Record<string, unknown>} */ (item);
                    if (!Object.prototype.hasOwnProperty.call(rec, propKey)) continue;
                    out.set(mapKey(rec[propKey]), rec);
                }

                return out;
            },
        },

        extract: {
            /**
             * Extracts the numeric ID from a Shopify GID string.
             * @param {string} gid - e.g. "gid://shopify/Metaobject/166455443655"
             * @param {Object} [options]
             * @param {boolean} [options.stringify]
             * @returns {number|string|null}
             */
            idFromGid(gid, options = {}) {
                if (typeof gid !== 'string') return null;
                // Match everything after the last "/"
                const match = gid.match(/(\d+)(?:\/)?$/);
                if (!match) return null;
                const idStr = match[1];
                if (options && options.stringify) return idStr;
                const idNum = Number(idStr);
                return Number.isFinite(idNum) ? idNum : null;
            },

            /**
             * Own enumerable string keys whose value is `Object.is`-equal to `value` (shallow, not recursive).
             * Non-objects and `null` → `[]`. Symbol keys are not considered (same as `Object.keys`).
             * @param {unknown} obj
             * @param {unknown} value
             * @returns {string[]}
             */
            keysByValue(obj, value) {
                if (obj === null || typeof obj !== 'object') return [];
                const rec = /** @type {Record<string, unknown>} */ (obj);
                const out = [];
                for (const key of Object.keys(rec)) {
                    if (Object.is(rec[key], value)) out.push(key);
                }
                return out;
            },
        },

        transform: {
            /** @param {unknown} v */
            boolean: (v) => {
                if (typeof v === 'boolean') return v;
                if (typeof v === 'number') return v !== 0 && !Number.isNaN(v);

                const str = normLower(v);
                if (!str) return false;

                if (['false', '0', 'no', 'off', 'none', 'null', 'undefined'].includes(str)) return false;
                if (['true', '1', 'yes', 'on', 'y', 't'].includes(str)) return true;

                return true; // any non-empty string
            },

            // Returns a Date or null
            /** @param {unknown} v */
            date: (v) => parseAnyDate(v),

            // Returns epoch ms (number) or NaN
            /** @param {unknown} v */
            ms: (v) => {
                const d = parseAnyDate(v);
                return d ? d.getTime() : NaN;
            },

            // Returns epoch seconds (number) or NaN
            /** @param {unknown} v */
            s: (v) => {
                const ms = util.transform.ms(v);
                return Number.isFinite(ms) ? Math.floor(ms / 1000) : NaN;
            },

            // Convenience: standard YYYY-MM-DD in local time
            /** @param {unknown} v */
            yyyyMmDd: (v) => {
                const d = parseAnyDate(v);
                if (!d) return '';
                const y = d.getFullYear();
                const m = pad2(d.getMonth() + 1);
                const day = pad2(d.getDate());
                return `${y}-${m}-${day}`;
            },
            /**
             * Shallow: new plain object without own enumerable keys matching omit rules (default: `null` only).
             * Not a plain object (arrays, primitives, `null`, class instances, dates, etc.) → returned unchanged.
             * Options: `deep` — recurse into nested plain objects and arrays; `omitEmptyArrays` — drop keys whose value is `[]`;
             * `omitEmptyStrings` — drop keys whose value is exactly `""` (not whitespace-trimmed).
             * Array elements are never removed; only object properties are omitted.
             * @param {unknown} v
             * @param {{
             *   deep?: boolean,
             *   omitEmptyArrays?: boolean,
             *   omitEmptyStrings?: boolean,
             * }} [options]
             */
            cleanJson: (v, options = {}) => cleanJson(v, options),
            /** @param {unknown} v */
            cssEscape: (v) => {
                const value = normStr(v);
                if (!value) return '';

                if (window.CSS?.escape) {
                    return window.CSS.escape(value);
                }

                return value.replace(/([ #;?%&,.+*~\':"!^$[\]()=>|/@])/g, '\\$1');
            },
        },

        check: {
            /** @param {unknown} v */
            isTrue: (v) => util.transform.boolean(v) === true,
            /** @param {unknown} v */
            isFalse: (v) => util.transform.boolean(v) === false,
        },

        compare: {
            /** @param {unknown} a @param {unknown} b */
            strict: (a, b) => normStr(a) === normStr(b),
            /** @param {unknown} a @param {unknown} b */
            lowercase: (a, b) => normLower(a) === normLower(b),

            object: {
                /**
                 * Structural equality for JSON-like plain objects and arrays (own enumerable string keys only).
                 * Primitives use `Object.is`. `Date` values compare by finite timestamp.
                 * Other object types compare by reference only (no `Map`/`Set` value equality).
                 * Circular references are not handled and may recurse until stack overflow.
                 *
                 * @param {unknown} a
                 * @param {unknown} b
                 * @param {{ deep?: boolean, loose?: boolean }} [options]
                 *   outermost plain object or array is expanded; nested plain objects/arrays use `Object.is`,
                 *   except paired `Date` instances which still compare by timestamp.
                 * @returns {boolean}
                 */
                equal: (a, b, options = {}) => {
                    const deep = options.deep !== false;
                    const loose = options.loose === true;
                
                    /** @param {unknown} x @param {unknown} y @returns {boolean} */
                    const valueSame = (x, y) => {
                        if (Object.is(x, y)) return true;
                        if (!loose) return false;
                
                        const nx = util.normalize.number(x);
                        const ny = util.normalize.number(y);
                
                        if (Number.isFinite(nx) && Number.isFinite(ny)) {
                            return nx === ny;
                        }
                
                        return normStr(x) === normStr(y);
                    };
                
                    /** @param {unknown} x @param {unknown} y @returns {boolean} */
                    const sameRefOrDate = (x, y) => {
                        if (valueSame(x, y)) return true;
                        if (x instanceof Date && y instanceof Date) {
                            const tx = x.getTime();
                            const ty = y.getTime();
                            return Number.isFinite(tx) && tx === ty;
                        }
                        return false;
                    };
                
                    /** @param {unknown} x @param {unknown} y @returns {boolean} */
                    const deepSame = (x, y) => {
                        if (sameRefOrDate(x, y)) return true;
                
                        if (Array.isArray(x) && Array.isArray(y)) {
                            if (x.length !== y.length) return false;
                            for (let i = 0; i < x.length; i++) {
                                if (!deepSame(x[i], y[i])) return false;
                            }
                            return true;
                        }
                
                        if (isPlainObject(x) && isPlainObject(y)) {
                            const ox = /** @type {Record<string, unknown>} */ (x);
                            const oy = /** @type {Record<string, unknown>} */ (y);
                            const keysX = Object.keys(ox);
                            const keysY = Object.keys(oy);
                
                            if (keysX.length !== keysY.length) return false;
                
                            const setY = new Set(keysY);
                
                            for (const k of keysX) {
                                if (!setY.has(k)) return false;
                                if (!deepSame(ox[k], oy[k])) return false;
                            }
                
                            return true;
                        }
                
                        return false;
                    };
                
                    /** @param {unknown} x @param {unknown} y @returns {boolean} */
                    const shallowSame = (x, y) => {
                        if (sameRefOrDate(x, y)) return true;
                
                        if (Array.isArray(x) && Array.isArray(y)) {
                            if (x.length !== y.length) return false;
                            for (let i = 0; i < x.length; i++) {
                                if (!sameRefOrDate(x[i], y[i])) return false;
                            }
                            return true;
                        }
                
                        if (isPlainObject(x) && isPlainObject(y)) {
                            const ox = /** @type {Record<string, unknown>} */ (x);
                            const oy = /** @type {Record<string, unknown>} */ (y);
                            const keysX = Object.keys(ox);
                            const keysY = Object.keys(oy);
                
                            if (keysX.length !== keysY.length) return false;
                
                            const setY = new Set(keysY);
                
                            for (const k of keysX) {
                                if (!setY.has(k)) return false;
                                if (!sameRefOrDate(ox[k], oy[k])) return false;
                            }
                
                            return true;
                        }
                
                        return false;
                    };
                
                    return deep ? deepSame(a, b) : shallowSame(a, b);
                },
            },

            array: {
                /**
                 * Multiset equality: same length and same elements ignoring order. Non-arrays → false.
                 * Default: each item uses `normLower` (trim + lowercase).
                 * `normalizeText: true` → `util.normalize.text` then lowercase (collapse internal whitespace).
                 * @param {unknown} a
                 * @param {unknown} b
                 * @param {{ normalizeText?: boolean }} [options]
                 * @returns {boolean}
                 */
                unorderedEqual: (a, b, options = {}) => {
                    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
                    const normalizeText = options.normalizeText === true;
                    /** @param {unknown} x */
                    const key = (x) =>
                        normalizeText
                            ? util.normalize.text(x).toLowerCase()
                            : normLower(x);
                    const sa = a.map(key).sort();
                    const sb = b.map(key).sort();
                    for (let i = 0; i < sa.length; i++) {
                        if (sa[i] !== sb[i]) return false;
                    }
                    return true;
                },
            },
            number: {
                /** @param {unknown} a @param {unknown} b */
                isGreaterThan: (a, b) => {
                    const A = toNumber(a);
                    const B = toNumber(b);
                    return isFiniteNumber(A) && isFiniteNumber(B) && A > B;
                },
                /** @param {unknown} a @param {unknown} b */
                isGreaterThanOrEqual: (a, b) => {
                    const A = toNumber(a);
                    const B = toNumber(b);
                    return isFiniteNumber(A) && isFiniteNumber(B) && A >= B;
                },
                /** @param {unknown} a @param {unknown} b */
                isLessThan: (a, b) => {
                    const A = toNumber(a);
                    const B = toNumber(b);
                    return isFiniteNumber(A) && isFiniteNumber(B) && A < B;
                },
                /** @param {unknown} a @param {unknown} b */
                isLessThanOrEqual: (a, b) => {
                    const A = toNumber(a);
                    const B = toNumber(b);
                    return isFiniteNumber(A) && isFiniteNumber(B) && A <= B;
                },
                /** @param {unknown} t @param {unknown} start @param {unknown} end @param {boolean} [inclusive=true] */
                inRange: (t, start, end, inclusive = true) => {
                    const T = toNumber(t);
                    const S = start != null && start !== '' ? toNumber(start) : NaN;
                    const E = end != null && end !== '' ? toNumber(end) : NaN;

                    if (!isFiniteNumber(T)) return false;

                    const hasS = isFiniteNumber(S);
                    const hasE = isFiniteNumber(E);

                    if (!hasS && !hasE) return true;
                    if (hasS && !hasE) return inclusive ? T >= S : T > S;
                    if (!hasS && hasE) return inclusive ? T <= E : T < E;

                    return inclusive ? (T >= S && T <= E) : (T > S && T < E);
                },
                /** Returns the greater of two numbers, or NaN if neither is valid.
                 *  @param {unknown} a
                 *  @param {unknown} b
                 *  @returns {number}
                 */
                max: (a, b) => {
                    const A = toNumber(a);
                    const B = toNumber(b);
                    if (isFiniteNumber(A) && isFiniteNumber(B)) return Math.max(A, B);
                    if (isFiniteNumber(A)) return A;
                    if (isFiniteNumber(B)) return B;
                    return NaN;
                },
                /** Returns the lesser of two numbers, or NaN if neither is valid.
                 *  @param {unknown} a
                 *  @param {unknown} b
                 *  @returns {number}
                 */
                min: (a, b) => {
                    const A = toNumber(a);
                    const B = toNumber(b);
                    if (isFiniteNumber(A) && isFiniteNumber(B)) return Math.min(A, B);
                    if (isFiniteNumber(A)) return A;
                    if (isFiniteNumber(B)) return B;
                    return NaN;
                },
                /** Returns the maximum number in an array-like input, or NaN if invalid/empty.
                 *  @param {unknown} arr
                 *  @returns {number}
                 */
                maxIn: (arr) => {
                    if (!arr || typeof arr[Symbol.iterator] !== 'function') return NaN;
                    let max = NaN;
                    for (const v of arr) {
                        const n = toNumber(v);
                        if (isFiniteNumber(n)) max = isFiniteNumber(max) ? Math.max(max, n) : n;
                    }
                    return max;
                },
                /** Returns the minimum number in an array-like input, or NaN if invalid/empty.
                 *  @param {unknown} arr
                 *  @returns {number}
                 */
                minIn: (arr) => {
                    if (!arr || typeof arr[Symbol.iterator] !== 'function') return NaN;
                    let min = NaN;
                    for (const v of arr) {
                        const n = toNumber(v);
                        if (isFiniteNumber(n)) min = isFiniteNumber(min) ? Math.min(min, n) : n;
                    }
                    return min;
                },
            },

            // Time comparisons: accept anything util.transform.date can parse.
            time: {
                /** @param {unknown} a @param {unknown} b */
                isBefore: (a, b) => {
                    const A = util.transform.ms(a);
                    const B = util.transform.ms(b);
                    return isFiniteNumber(A) && isFiniteNumber(B) && A < B;
                },
                /** @param {unknown} a @param {unknown} b */
                isAfter: (a, b) => {
                    const A = util.transform.ms(a);
                    const B = util.transform.ms(b);
                    return isFiniteNumber(A) && isFiniteNumber(B) && A > B;
                },
                /** @param {unknown} t @param {unknown} start @param {unknown} end @param {boolean} [inclusive=true] */
                inRange: (t, start, end, inclusive = true) => {
                    const T = util.transform.ms(t);
                    const S = start != null && start !== '' ? util.transform.ms(start) : NaN;
                    const E = end != null && end !== '' ? util.transform.ms(end) : NaN;

                    if (!isFiniteNumber(T)) return false;

                    const hasS = isFiniteNumber(S);
                    const hasE = isFiniteNumber(E);

                    if (!hasS && !hasE) return true;
                    if (hasS && !hasE) return inclusive ? T >= S : T > S;
                    if (!hasS && hasE) return inclusive ? T <= E : T < E;

                    return inclusive ? (T >= S && T <= E) : (T > S && T < E);
                },
            },
        },
    };

    // Attach once and freeze
    Object.defineProperty(window, 'util', {
        value: Object.freeze(util),
        writable: false,
    });
})();
